import {
  BallFinalKinds,
  BaseballLineupPosition,
  baseballLogicService,
  CallTypes,
  constants,
  Dexterity,
  HitTypes,
  IApiGameState,
  IApiLineupPlayer,
  IApiPlayResult,
  IApiTaggerBehavior,
  IApiTaggingPlayers,
  KOrBB,
  PitchTypes,
  ResultTypes,
  StrikeZoneDefinitionEnum,
  TopBottom,
} from '@trackman/baseball-shared-components';
import { BroadcastItems } from 'enums/BroadcastItems';
import { SessionType } from 'enums/SessionType';
import { IApiBallFinal } from 'interfaces/broadcast/IApiBallFinal';
import { IBroadcastBase } from 'interfaces/broadcast/IBroadcastBase';
import { IApiPlayWithBallFinals } from 'interfaces/session/IApiPlayWithBallFinals';
import { IStrikeZoneDefinition } from 'interfaces/session/IStrikeZoneDefinition';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { IApiStadiumPlayModel, ICustomMetadata } from 'interfaces/tagging/ApiModels/IApiStadiumPlayModel';
import { IInning } from 'interfaces/tagging/IInning';
import { toJS } from 'mobx';
import { AdjustedValue } from 'models/tagging/AdjustedValue';
import { BpPlay } from 'models/tagging/BpPlay';
import { Play } from 'models/tagging/Play';
import { PlayLineup } from 'models/tagging/PlayLineup';
import moment from 'moment';
import mappingHelpers from 'services/helper-services/mappingHelpers/mappingHelpers';
import { v1 as uuid } from 'uuid';
import { IStrikeZoneDecision } from '../../interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { IApiBallFinalPitch } from './../../interfaces/broadcast/IApiBallFinal';

export const createPlayBasedOnReferencePlay = (referencePlay: Play, insertBeforeReference: boolean) => {
  const play = new Play(uuid());
  const getValue = (reference: AdjustedValue) => toJS(insertBeforeReference ? reference.initial : reference.total);
  play.inningNumber = toJS(referencePlay.inningNumber);
  play.isTopInning = toJS(referencePlay.isTopInning);
  play.homeTeamScore.initial = getValue(referencePlay.homeTeamScore);
  play.awayTeamScore.initial = getValue(referencePlay.awayTeamScore);
  play.balls.initial = getValue(referencePlay.balls);
  play.strikes.initial = getValue(referencePlay.strikes);
  play.outs.initial = getValue(referencePlay.outs);
  play.pitcher = toJS(referencePlay.pitcher);
  play.batter = toJS(referencePlay.batter);
  play.catcher = toJS(referencePlay.catcher);
  play.firstBaseman = toJS(referencePlay.firstBaseman);
  play.secondBaseman = toJS(referencePlay.secondBaseman);
  play.thirdBaseman = toJS(referencePlay.thirdBaseman);
  play.centerFielder = toJS(referencePlay.centerFielder);
  play.leftFielder = toJS(referencePlay.leftFielder);
  play.rightFielder = toJS(referencePlay.rightFielder);
  play.shortStopper = toJS(referencePlay.shortStopper);
  play.sequenceNumber = toJS(insertBeforeReference ? referencePlay.sequenceNumber : referencePlay.sequenceNumber + 1);
  play.playLineup = new PlayLineup(referencePlay.playLineup.homeTeam.players, referencePlay.playLineup.awayTeam.players);
  return play;
};

export const setPitcherAndBatterData = (
  play: Play,
  lastPlay: Play | undefined,
  shouldProgressBatter: boolean,
  manuallyChangedPlayerAtBat: IApiLineupPlayer | undefined
) => {
  play.pitcher = toJS(play.getPitchingTeamLineup().getPlayer(BaseballLineupPosition.Pitcher));
  play.catcher = toJS(play.getPitchingTeamLineup().getPlayer(BaseballLineupPosition.Catcher));
  if (manuallyChangedPlayerAtBat) {
    play.batter = manuallyChangedPlayerAtBat;
  } else {
    if (!lastPlay || !play.getBattingTeamLineup().isPlayerInLineup(lastPlay.batter.id)) {
      play.batter = toJS(play.getBattingTeamLineup().getNextBatter(undefined));
    } else if (shouldProgressBatter) {
      play.batter = toJS(play.getBattingTeamLineup().getNextBatter(lastPlay.batter.id));
    } else {
      play.batter = toJS(lastPlay.batter);
    }
  }
};

export const setDefensivePlayersData = (play: Play): void => {
  play.firstBaseman = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.FirstBase));
  play.secondBaseman = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.SecondBase));
  play.thirdBaseman = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.ThirdBase));
  play.centerFielder = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.CenterField));
  play.leftFielder = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.LeftField));
  play.rightFielder = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.RightField));
  play.shortStopper = toJS(play.getPitchingTeamLineup().getDefensivePlayer(BaseballLineupPosition.Shortstop));
};

const isBroadcastItemBallFinal = (item: IBroadcastBase, broadcastItemType: BroadcastItems): item is IApiBallFinal => {
  return broadcastItemType === BroadcastItems.BallFinal;
};
const isBroadcastItemPitchLive = (item: IBroadcastBase, broadcastItemType: BroadcastItems): item is IApiBallFinalPitch => {
  return broadcastItemType === BroadcastItems.PitchLive;
};

export const updatePlayWithBroadcastValues = (
  play: Play,
  broadcastItem: IBroadcastBase,
  broadcastItemType: BroadcastItems,
  isRoboUmpEnabled: boolean,
  strikeZoneConfig: IStrikeZoneDefinition
) => {
  if (isBroadcastItemBallFinal(broadcastItem, broadcastItemType)) {
    play.addBallFinal(broadcastItem);
    switch (broadcastItem.kind) {
      case BallFinalKinds.Pitch:
        play.pitchBallFinalTrackId = broadcastItem.trackId;
        play.trackStartTimeUtc = broadcastItem.trackStartTime;
        break;
      case BallFinalKinds.Hit:
        play.hitBallFinalTrackId = broadcastItem.trackId;
        break;
      case BallFinalKinds.CatcherThrow:
        play.throwBallFinalTrackId = broadcastItem.trackId;
        break;
    }
  }

  /**
   * If RoboUmp is disabled - take values from PitchLive and set StrikeZone to Static as default
   */
  if (!isRoboUmpEnabled) {
    const inchesToMeters = (value: number) => {
      return value * 0.0254;
    };

    if (isBroadcastItemPitchLive(broadcastItem, broadcastItemType)) {
      if (broadcastItem.location.position.z && broadcastItem.location.position.y) {
        play.strikeZoneDecision = {
          ballX: broadcastItem.location.position.z,
          ballY: broadcastItem.location.position.y,
          isInZone: false,
          source: 'BallFinal',
          strikeZoneType: StrikeZoneDefinitionEnum.Static,
          dimensions: {
            left: strikeZoneConfig.staticStrikeZoneWidth ? -inchesToMeters(strikeZoneConfig.staticStrikeZoneWidth / 2) : 0.037338,
            right: strikeZoneConfig.staticStrikeZoneWidth ? inchesToMeters(strikeZoneConfig.staticStrikeZoneWidth / 2) : 0.501904,
            top: strikeZoneConfig.staticStrikeZoneTop ? inchesToMeters(strikeZoneConfig.staticStrikeZoneTop) : -0.2159,
            bottom: strikeZoneConfig.staticStrikeZoneBottom ? inchesToMeters(strikeZoneConfig.staticStrikeZoneBottom) : 0.2159,
            ballRadius: strikeZoneConfig.ballDiameter ? inchesToMeters(strikeZoneConfig.ballDiameter / 2) : 1.082294,
          },
          strikeZoneDecisionLocations: [],
        };
      }
    }
  }
};

export const updatePlayWithStrikeZoneDecision = (play: Play | BpPlay, strikeZoneDecision: IStrikeZoneDecision) => {
  if (!play.strikeZoneDecision || play.strikeZoneDecision.source == 'PitchLive') {
    play.strikeZoneDecision = strikeZoneDecision;
  }
};

export const mergePlaysWithEmptyPlaysAndFixComputedProperties = (
  dbPlays: IApiPlayWithBallFinals[],
  emptyPlays: IApiPlayWithBallFinals[],
  sessionMetadata: IApiSessionMetadataUpdated
) => {
  fixPlays(dbPlays, sessionMetadata);
  fixPlays(emptyPlays, sessionMetadata);
  const result = [...dbPlays];
  result.sort(
    (a: IApiPlayWithBallFinals, b: IApiPlayWithBallFinals) => a.play.taggerBehavior.sequenceNumber - b.play.taggerBehavior.sequenceNumber
  );

  const getEarliestTrackStartTime = (play: IApiPlayWithBallFinals) => moment.min(play.ballFinals.map((b) => moment(b.trackStartTime)));
  for (let i = 0; i < emptyPlays.length; i++) {
    const play = { ...emptyPlays[i] };
    play.isChanged = true;
    play.play.taggerBehavior.isWarmup = true; // by default plays created automatically from ball finals are warmup
    const playStartTime = getEarliestTrackStartTime(play);
    if (result.length === 0) {
      result.push(play);
      continue;
    }
    for (let j = 0; j < result.length; j++) {
      if (result[j].ballFinals.length === 0) {
        // manual play
        if (j === result.length - 1) {
          // this is the last play
          result.push(play); // insert at the end
          break;
        }
        continue; // skip manual plays as we can't be sure about their start time
      }
      if (moment(getEarliestTrackStartTime(result[j])).isAfter(playStartTime)) {
        result.splice(j, 0, play);
        break;
      } else if (j === result.length - 1) {
        // this is the last play
        result.push(play); // insert at the end
        break;
      }
    }
  }

  if (sessionMetadata.sessionType === SessionType.BattingPractice) {
    result.forEach((p) => {
      p.play.gameState.topBottom = TopBottom.Bottom;
      p.play.gameState.isHomeTeamBatting = true;
    });
  }
  return result;
};
const fixPlays = (plays: IApiPlayWithBallFinals[], sessionMetadata: IApiSessionMetadataUpdated) => {
  for (let i = 0; i < plays.length; i++) {
    const play = plays[i];
    if (!play.play.taggerBehavior) {
      play.play.taggerBehavior = {} as IApiTaggerBehavior;
    }

    if (!play.play.players) {
      play.play.players = {} as IApiTaggingPlayers;
    }

    if (!play.play.players.batter) {
      play.play.players.batter = {
        ...constants.emptyPlayer,
        battingHandedness: Dexterity.Undefined,
      };
    }
    if (!play.play.players.pitcher) {
      play.play.players.pitcher = {
        ...constants.emptyPlayer,
        pitchingHandedness: Dexterity.Undefined,
      };
    }
    if (!play.play.players.catcher) {
      play.play.players.catcher = {
        ...constants.emptyPlayer,
        pitchingHandedness: Dexterity.Undefined,
      };
    }
    if (!play.play.gameState) {
      play.play.gameState = {
        inning: 1,
        topBottom: TopBottom.Top,
      } as IApiGameState;
    }
    if (play.play.gameState.inning === 0) {
      play.play.gameState.inning = 1;
    }
    if (!play.play.playResult) {
      play.play.playResult = {
        outs: 0,
        runs: 0,
        result: ResultTypes.Undefined,
      } as IApiPlayResult;
    }
    if (!play.play.pitchTag) {
      play.play.pitchTag = { ...constants.emptyPitchTag };
    }
    if (!play.play.hitTag) {
      play.play.hitTag = { ...constants.emptyHitTag };
    }
    if (!play.play.hitTag.hitType) {
      play.play.hitTag.hitType = HitTypes.Undefined;
    }
    if (!play.play.catcherThrowTag) {
      play.play.catcherThrowTag = { ...constants.emptyCatcherThrowTag };
    }
    if (!play.play.customMetadata) {
      play.play.customMetadata = {} as ICustomMetadata;
    }
    if (!play.play.playLineup) {
      play.play.playLineup = {
        homeTeam: [...(sessionMetadata.homeTeam?.lineup ?? [])],
        awayTeam: [...(sessionMetadata.awayTeam?.lineup ?? [])],
      };
    }
    if (!play.play.note) {
      play.play.note = '';
    }
    if (!play.play.kOrBB) {
      play.play.kOrBB = KOrBB.Undefined;
    }
  }
};

export const createEmptyReviewPlay = (sessionMetadata: IApiSessionMetadataUpdated) => {
  const play = {
    play: {
      id: uuid(),
      taggerBehavior: {
        manuallyAddedByTagger: true,
      },
      pitchTag: {
        pitchCall: CallTypes.Undefined,
        pitchType: PitchTypes.Undefined,
        pitcherSet: constants.undefinedValue,
      },
      hitTag: {
        hitType: HitTypes.Undefined,
      },
      playResult: {
        result: ResultTypes.Undefined,
      },
      kOrBB: constants.undefinedValue,
      note: '',
    } as IApiStadiumPlayModel,
    ballFinals: [],
    isChanged: true,
  } as IApiPlayWithBallFinals;
  fixPlays([play], sessionMetadata);
  return play;
};

export const recalculateComputedPlayProperties = (inningsReversed: IInning[], metadata: IApiSessionMetadataUpdated) => {
  const updatedEntries = [];
  const apiPlays = [];
  for (let i = inningsReversed.length - 1; i >= 0; i--) {
    for (let j = inningsReversed[i].playsReversed.length - 1; j >= 0; j--) {
      const play = inningsReversed[i].playsReversed[j];
      apiPlays.push(mappingHelpers.playToApiPlayModelMap(play, metadata));
    }
  }
  const fixedApiPlays = baseballLogicService.recalculateApiPlayComputedProperties(apiPlays);
  let idx = 0;
  for (let i = inningsReversed.length - 1; i >= 0; i--) {
    for (let j = inningsReversed[i].playsReversed.length - 1; j >= 0; j--) {
      const play = inningsReversed[i].playsReversed[j];
      const { taggerBehavior } = fixedApiPlays[idx];
      idx++;
      if (
        taggerBehavior.pAofInning != play.pAofInning ||
        taggerBehavior.pitchOfPA != play.pitchOfPA ||
        taggerBehavior.playSequenceNumber != play.playSequenceNumber ||
        taggerBehavior.sequenceNumber != play.sequenceNumber
      ) {
        play.pAofInning = taggerBehavior.pAofInning;
        play.pitchOfPA = taggerBehavior.pitchOfPA;
        play.playSequenceNumber = taggerBehavior.playSequenceNumber;
        play.sequenceNumber = taggerBehavior.sequenceNumber;
        updatedEntries.push(play);
      }
    }
  }
  return updatedEntries;
};
